import { FriendlyNameService } from '../../types/PanoramaArrowIdentifier';
import { AbstractAdaptiveHotspot } from './AbstractAdaptiveHotspot';
import he from 'he';
import { CameraPosition } from '../../../../panorama/Positions';
import { NOT_TILTED_ICON_RESIZE_FACTOR } from '../../types/consts';
/***
 * Represents a hotspot that is able to render or hide itself on virtual tour depending on current scene.
 */
export class SimpleBaseHotspot extends AbstractAdaptiveHotspot {
    constructor(coordinates, _zOrder = 0, _iconBundle, id = FriendlyNameService.generateRandomId()) {
        super(coordinates.virtualTourScene, id);
        this._zOrder = _zOrder;
        this._iconBundle = _iconBundle;
        this._krpanoHotspot = null;
        this._krpanoLayer = null;
        this._isDragAndDropAllowed = false;
        this._backgroundImage = undefined;
        this._label = null;
        this._hasTilt = false;
        this.onDragAndDropStarted = undefined;
        this.onBeingDragged = undefined;
        this.onDragAndDropEnded = undefined;
        this.updateLabel = (label) => {
            this._label = label;
            if (this._virtualTour && this._krpanoHotspot) {
                this._renderLabel(this._virtualTour, this._krpanoHotspot);
            }
        };
        /** Updates hotspot to display image. */
        this.updateBackgroundImage = (backgroundImage, offsetY) => {
            this._backgroundImage = backgroundImage;
            if (this._krpanoHotspot && this._virtualTour) {
                this._adjustHotspotWithBackground(this._virtualTour, this._krpanoHotspot, offsetY);
            }
        };
        /** Updates hotspot to display icon. */
        this.updateIcon = (iconId, hasTilt) => {
            const urls = hasTilt ? this._iconBundle.icons.tiltedUrls : this._iconBundle.icons.urls;
            this._backgroundImage = {
                url: urls[iconId],
                width: hasTilt ? 44 : 88 / NOT_TILTED_ICON_RESIZE_FACTOR,
                height: hasTilt ? 18 : 36 / NOT_TILTED_ICON_RESIZE_FACTOR,
            };
            this._hasTilt = hasTilt !== null && hasTilt !== void 0 ? hasTilt : false;
            this.updateBackgroundImage(this._backgroundImage, hasTilt ? -4 : 0);
        };
        this.updateOnClick = (handler) => {
            this._onClick = handler;
            if (this._krpanoHotspot) {
                this._krpanoHotspot.onclick = this._onClick;
            }
        };
        /** Updates the coordinates of the hotspot in the scene. */
        this.updateSceneCoordinates = (sceneCoordinates) => {
            this._sceneCoordinates = sceneCoordinates;
            if (this._krpanoHotspot) {
                this._krpanoHotspot.ath = this._sceneCoordinates.x;
                this._krpanoHotspot.atv = this._sceneCoordinates.y;
            }
        };
        /** Updates hotspot to use drag and drop */
        this.updateIsDragAndDropAllowed = (value) => {
            this._isDragAndDropAllowed = value;
            if (!this._krpanoHotspot) {
                return;
            }
            if (!this._isDragAndDropAllowed) {
                this._krpanoHotspot.ondown = undefined;
                return;
            }
            if (this._virtualTour) {
                this._adjustHotspotWithDraggable(this._virtualTour, this._krpanoHotspot);
            }
        };
        /** Moves the virtual tour so that it looks at the hotspot. */
        this.lookTo = () => {
            if (!this._virtualTour) {
                throw Error('Cannot lookTo without attaching to a virtual tour.');
            }
            const lookTo = new CameraPosition(this._sceneCoordinates.x, this._sceneCoordinates.y, 60, this._virtualTourPosition.sceneIndex, this._virtualTourPosition.sceneGuid);
            this._virtualTour.lookToCameraPosition(lookTo);
        };
        this._adjustHotspotWithDraggable = (virtualTour, hotspot) => {
            const krpano = virtualTour._pano;
            hotspot.ondown = () => {
                var _a;
                const hs_screen = krpano.spheretoscreen(hotspot.ath, hotspot.atv);
                // get the offset from the hotspot center to the mouse cursor
                const offsetx = krpano.mouse.stagex - hs_screen.x;
                const offsety = krpano.mouse.stagey - hs_screen.y;
                (_a = this.onDragAndDropStarted) === null || _a === void 0 ? void 0 : _a.call(this);
                krpano.actions.asyncloop(() => {
                    var _a, _b;
                    const isPressed = hotspot.pressed;
                    (_a = this.onBeingDragged) === null || _a === void 0 ? void 0 : _a.call(this, { x: hotspot.ath, y: hotspot.atv });
                    if (!isPressed) {
                        const newCoordinates = { x: hotspot.ath, y: hotspot.atv };
                        this.updateSceneCoordinates(newCoordinates);
                        (_b = this.onDragAndDropEnded) === null || _b === void 0 ? void 0 : _b.call(this, { sceneCoordinates: newCoordinates });
                        return false;
                    }
                    return true;
                }, () => {
                    const pt = krpano.screentosphere(krpano.mouse.stagex - offsetx, krpano.mouse.stagey - offsety);
                    hotspot.ath = pt.x;
                    hotspot.atv = pt.y;
                });
            };
        };
        this._adjustHotspotWithBackground = (virtualTour, hotspot, offsetY) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            const isRetinaDevice = virtualTour._pano.Device.isMacOrRetina();
            const url = isRetinaDevice
                ? ((_b = (_a = this._backgroundImage) === null || _a === void 0 ? void 0 : _a.urlRetina) !== null && _b !== void 0 ? _b : (_c = this._backgroundImage) === null || _c === void 0 ? void 0 : _c.url)
                : (_d = this._backgroundImage) === null || _d === void 0 ? void 0 : _d.url;
            hotspot.url = url;
            const cropValue = isRetinaDevice
                ? ((_f = (_e = this._backgroundImage) === null || _e === void 0 ? void 0 : _e.cropRetina) !== null && _f !== void 0 ? _f : (_g = this._backgroundImage) === null || _g === void 0 ? void 0 : _g.crop)
                : (_h = this._backgroundImage) === null || _h === void 0 ? void 0 : _h.crop;
            if (cropValue) {
                hotspot.crop = cropValue;
            }
            if ((_j = this._backgroundImage) === null || _j === void 0 ? void 0 : _j.width) {
                hotspot.width = this._backgroundImage.width;
            }
            if ((_k = this._backgroundImage) === null || _k === void 0 ? void 0 : _k.height) {
                hotspot.height = this._backgroundImage.height;
            }
            if (offsetY !== undefined) {
                hotspot.oy = offsetY;
            }
        };
        this._renderLabel = (virtualTour, hotspot) => {
            var _a;
            if (!this._label) {
                virtualTour.getKrpanoGlobalObj().removelayer(this._getLayerId());
                return;
            }
            const krpano = virtualTour._pano;
            const layer = (_a = krpano.getLayer(this._getLayerId())) !== null && _a !== void 0 ? _a : krpano.addLayer(this._getLayerId());
            this._krpanoLayer = layer;
            this._setLayerHtml(this._krpanoLayer, this._label);
            layer.type = 'text';
            layer.renderer = 'css3d';
            layer.zorder = 0;
            layer.parent = `hotspot[${hotspot.name}]`;
            layer.bg = false;
            layer.align = 'center';
            layer.textshadow = 0.1;
            layer.textshadowrange = 5.0;
            layer.textshadowangle = 45;
            layer.textshadowcolor = '0x000000';
            layer.textshadowalpha = 0.8;
            layer.y = 45;
            layer.onclick = hotspot.onclick;
        };
        this._getLayerId = () => {
            return FriendlyNameService.combine(this._id, FriendlyNameService.getFriendlyName('Label'));
        };
        this._sceneCoordinates = coordinates.sceneCoordinates;
    }
    render() {
        var _a;
        if (!this._virtualTour) {
            return;
        }
        const krpano = this._virtualTour._pano;
        this._krpanoHotspot =
            (_a = krpano.getKrpanoHotspot(this._id)) !== null && _a !== void 0 ? _a : krpano.addKrpanoHotspot(this._id);
        this._krpanoHotspot.zorder = this._zOrder;
        this._krpanoHotspot.renderer = 'css3d';
        this.updateSceneCoordinates(this._sceneCoordinates);
        this.updateIsDragAndDropAllowed(this._isDragAndDropAllowed);
        this.updateBackgroundImage(this._backgroundImage, this._hasTilt ? -4 : 0);
        this.updateOnClick(this._onClick);
        this._renderLabel(this._virtualTour, this._krpanoHotspot);
    }
    hide() {
        if (!this._virtualTour) {
            return;
        }
        this._krpanoHotspot = null;
        this._virtualTour.getKrpanoGlobalObj().removehotspot(this._id);
        this._virtualTour.getKrpanoGlobalObj().removelayer(this._getLayerId());
    }
    detachCore() {
        this.onDragAndDropStarted = undefined;
        this.onBeingDragged = undefined;
        this.onDragAndDropEnded = undefined;
    }
    _setLayerHtml(layer, label) {
        layer.html = `[font color="#FFFFFF" style="font-size:14px"]${he.encode(label)}[/font]`;
    }
}
